import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { registry } from '../../core/Registry.js';

const Wrapper = styled.div`
  max-width: 800px;
  width: ${props => props.width || '100%'};
  margin: ${props => props.align === 'center' ? '0 auto' : (props.align === 'right' ? '0 0 0 auto' : '0')};
  background: var(--glass-1);
  border: 1px solid var(--glass-border);
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  height: 400px;
  backdrop-filter: blur(10px);

  @media(max-width: 768px) {
    flex-direction: column-reverse;
    height: auto;
  }
`;

const Sidebar = styled.div`
  width: 300px;
  background: var(--glass-2);
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--glass-border);

  @media(max-width: 768px) {
    width: 100%;
    border-right: none;
    border-top: 1px solid var(--glass-border);
  }
`;

const SearchBox = styled.div`
  padding: 20px;
  border-bottom: 1px solid var(--glass-border);

  input {
    width: 100%;
    padding: 12px;
    background: rgba(0,0,0,0.2);
    border: 1px solid var(--glass-border);
    border-radius: 8px;
    color: var(--text-primary);
    outline: none;
    &::placeholder { color: var(--text-muted); }
    &:focus { border-color: var(--accent); }
  }
`;

const ShopList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 10px;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: var(--glass-border-hover); border-radius: 3px; }
`;

const ShopItem = styled.div`
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 10px;
  background: ${props => props.active ? 'rgba(6,182,212,0.1)' : 'transparent'};
  border: 1px solid ${props => props.active ? 'rgba(6,182,212,0.3)' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: var(--glass-1);
    border-color: var(--glass-border);
  }

  h4 { margin: 0 0 5px 0; font-size: 14px; color: var(--text-primary); }
  p { margin: 0; font-size: 12px; color: var(--text-secondary); }
  .dist { font-size: 11px; color: var(--accent); margin-top: 5px; font-weight: bold; display: flex; align-items: center; gap: 4px; }
  svg { width: 12px; height: 12px; }
`;

const MapArea = styled.div`
  flex: 1;
  background: #0f172a;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(var(--glass-border-hover) 1px, transparent 1px);
    background-size: 20px 20px;
    opacity: 0.3;
  }
`;

const MockPin = styled.div`
  position: absolute;
  top: ${props => props.top};
  left: ${props => props.left};
  width: 24px;
  height: 24px;
  background: var(--accent);
  border-radius: 50% 50% 50% 0;
  transform: rotate(-45deg);
  box-shadow: 0 0 15px var(--accent-glow);
  cursor: pointer;
  z-index: 2;

  &::after {
    content: '';
    width: 8px;
    height: 8px;
    background: #fff;
    border-radius: 50%;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &:hover { transform: rotate(-45deg) scale(1.2); }
`;

const ShopLocatorWidget = ({ node }) => {
  const [active, setActive] = useState(0);
  const [stores, setStores] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/stores')
      .then(r => r.json())
      .then(data => {
        setStores(data.stores || []);
        setLoading(false);
      })
      .catch(e => {
        console.error('Fehler beim Laden der Shops', e);
        setLoading(false);
      });
  }, []);

  const filteredShops = useMemo(() => {
    if (!search.trim()) return stores.slice(0, 10);
    const q = search.toLowerCase();
    return stores.filter(s => 
      s.stadt?.toLowerCase().includes(q) || 
      s.standort?.toLowerCase().includes(q) ||
      s.kette?.toLowerCase().includes(q)
    ).slice(0, 15);
  }, [stores, search]);

  return (
    <Wrapper align={node.style?.desktop?.align} width={node.style?.desktop?.width}>
      <Sidebar>
        <SearchBox>
          <input 
            type="text" 
            placeholder="PLZ oder Stadt eingeben..." 
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </SearchBox>
        <ShopList>
          {loading && <div style={{padding: 20, color: 'var(--text-muted)'}}>Lade Märkte...</div>}
          {!loading && filteredShops.length === 0 && <div style={{padding: 20, color: 'var(--text-muted)'}}>Keine Märkte gefunden.</div>}
          {filteredShops.map((s, i) => (
            <ShopItem key={i} active={active === i} onClick={() => setActive(i)}>
              <h4>{s.kette} {s.stadt}</h4>
              <p>{s.standort}</p>
              <div className="dist">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                {s.adresse || s.bundesland}
              </div>
            </ShopItem>
          ))}
        </ShopList>
      </Sidebar>
      <MapArea>
        {filteredShops.map((s, i) => {
          // Dummy Positions für Map-Pins basierend auf Index
          const top = `${20 + (i * 15 % 60)}%`;
          const left = `${30 + (i * 20 % 50)}%`;
          return (
            <MockPin key={i} top={top} left={left} style={{ opacity: active === i ? 1 : 0.5, zIndex: active === i ? 10 : 2 }} />
          );
        })}
        <div style={{zIndex: 1, fontWeight: 'bold', fontSize: '18px', opacity: 0.5}}>Interaktive Map</div>
      </MapArea>
    </Wrapper>
  );
};

registry.register('shop_locator', {
  label: 'O2 Shopsuche',
  icon: '📍',
  category: 'tef_specific',
  defaultContent: {},
  defaultStyle: { desktop: { align: 'center', width: '100%' } },
  render: ShopLocatorWidget,
  inspectorControls: [
    { tab: 'style', groups: [{ title: 'Dimensionen', fields: [{name:'style.desktop.align', label:'Ausrichtung', type:'select', options:[{label:'Zentriert', value:'center'},{label:'Links', value:'flex-start'}]}, {name:'style.desktop.width', label:'Max Breite', type:'text'}] }] }
  ]
});
